import { useMemo } from 'react';

import { PageSectionRenderer } from '@/components/page-sections/page-section-renderer';
import { PublicThemeWrapper } from '@/components/shared/public-theme-wrapper';
import { ScaledContent } from '@/components/ui/scaled-content';
import { resolveTemplatePreviewBlocks } from '@/lib/blocks/collection/data-widget/blocks';
import { resolveIndexCollectionSectionType } from '@/lib/blocks/collection/index-collection/registry';
import { type DefaultBlockInput, normalizeDefaultBlocks } from '@/lib/blocks/default-blocks';
import { readString } from '@/lib/prop-utils';
import { hasRichTextContent } from '@/lib/rich-text';
import { cn } from '@/lib/utils';
import type { PageBlock } from '@/types/block';
import type { SectionStructure } from '@/types/page';
import type { PageSection } from '@/types/page-section';
import type { SectionTemplateItem } from '@/types/section-template';

function pinnedPreviewProp(fromSection: unknown, fromTemplate: unknown) {
  const sectionText = readString(fromSection);
  return hasRichTextContent(sectionText) ? sectionText : readString(fromTemplate);
}

export interface PageTemplateFromApi {
  id: string;
  code: string;
  name: string;
  pageType: string;
  description: string | null;
  sectionsStructure: SectionStructure[];
}

interface TemplatePreviewCardProps {
  template: PageTemplateFromApi;
  templatesByCode: Map<string, SectionTemplateItem>;
  onClick: () => void;
  disabled?: boolean;
  className?: string;
  renderMode?: 'template_preview' | 'layout_preview';
}

function resolvePreviewBlocks(blocks: DefaultBlockInput[] | undefined) {
  if (!Array.isArray(blocks) || blocks.length === 0) return undefined;

  const pageBlocks = resolvePageBlocks(blocks);
  return resolveTemplatePreviewBlocks(pageBlocks);
}

function resolvePageBlocks(blocks: DefaultBlockInput[]): PageBlock[] {
  return blocks.every(isPageBlockInput)
    ? (blocks as unknown as PageBlock[])
    : normalizeDefaultBlocks(blocks);
}

function isPageBlockInput(block: DefaultBlockInput): boolean {
  return (
    typeof block.id === 'string' &&
    typeof block.blockType === 'string' &&
    typeof block.columnSpan === 'number' &&
    typeof block.position === 'number' &&
    typeof block.visible === 'boolean'
  );
}

function hasRenderableIndexCollectionBlocks(blocks: DefaultBlockInput[] | undefined): boolean {
  if (!Array.isArray(blocks) || blocks.length === 0) return false;

  const pageBlocks = resolvePageBlocks(blocks);
  const indexCollectionBlock = pageBlocks.find((block) => block.blockType === 'index_collection');
  if (!indexCollectionBlock) return false;

  const cardTemplateBlock = pageBlocks.find(
    (block) =>
      block.parentBlockId === indexCollectionBlock.id &&
      block.blockType === 'group' &&
      block.props?.role === 'card_template',
  );
  if (!cardTemplateBlock) return false;

  return pageBlocks.some((block) => block.parentBlockId === cardTemplateBlock.id);
}

export function resolveSectionPreviewBlocks({
  section,
  templateMeta,
}: {
  section: SectionStructure;
  templateMeta?: SectionTemplateItem;
}): DefaultBlockInput[] | undefined {
  const sectionBlocks =
    Array.isArray(section.blocks) && section.blocks.length > 0 ? section.blocks : undefined;
  const templateBlocks =
    Array.isArray(templateMeta?.defaultBlocks) && templateMeta.defaultBlocks.length > 0
      ? templateMeta.defaultBlocks
      : undefined;

  const indexSectionType = resolveIndexCollectionSectionType({
    sectionType: templateMeta?.sectionType ?? section.sectionType,
    sectionTemplateCode: section.sectionTemplateCode,
  });

  if (!indexSectionType) {
    return (sectionBlocks ?? templateBlocks) as DefaultBlockInput[] | undefined;
  }

  if (hasRenderableIndexCollectionBlocks(sectionBlocks as DefaultBlockInput[] | undefined)) {
    return sectionBlocks as DefaultBlockInput[];
  }

  return (templateBlocks ?? sectionBlocks) as DefaultBlockInput[] | undefined;
}

function resolveTemplatePreviewSections(
  sectionsStructure: SectionStructure[],
  templatesByCode: Map<string, SectionTemplateItem>,
): PageSection[] {
  return sectionsStructure.map((section, index) => {
    const templateMeta = templatesByCode.get(section.sectionTemplateCode);
    const variantValue = section.variant ?? templateMeta?.variants[0]?.value ?? 'default';

    const templateProps = section.props || {};
    const defaultProps = templateMeta?.defaultProps ?? {};
    const props = {
      ...defaultProps,
      ...templateProps,
      pinnedHeading: pinnedPreviewProp(templateProps.pinnedHeading, defaultProps.pinnedHeading),
      pinnedDescription: pinnedPreviewProp(
        templateProps.pinnedDescription,
        defaultProps.pinnedDescription,
      ),
    };
    const sectionBlocks = resolveSectionPreviewBlocks({ section, templateMeta });
    const normalizedBlocks = resolvePreviewBlocks(sectionBlocks);

    return {
      id: `preview-${section.sectionTemplateCode}-${index}`,
      sectionTemplateCode: section.sectionTemplateCode,
      sectionType: templateMeta?.sectionType ?? section.sectionType,
      props,
      variant: variantValue,
      visible: true,
      rowPosition: index,
      blocks: normalizedBlocks,
      permissions: section.permissions,
    };
  });
}

export function TemplatePreviewCard({
  template,
  templatesByCode,
  onClick,
  disabled = false,
  className,
  renderMode = 'template_preview',
}: TemplatePreviewCardProps) {
  const sectionPreviews = useMemo(
    () => resolveTemplatePreviewSections(template.sectionsStructure, templatesByCode),
    [template.sectionsStructure, templatesByCode],
  );

  const handleClick = () => {
    if (!disabled) {
      onClick();
    }
  };

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: This section wraps a live preview that can contain real <button> so we cannot use a semantic <button> here without nesting buttons.
    <section
      onClick={handleClick}
      aria-label={template.name}
      className={cn(
        'relative overflow-hidden border-2',
        'hover:border-primary transition-colors',
        'text-left w-full',
        'flex flex-col',
        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
        className,
      )}
    >
      <ScaledContent className="relative bg-muted/20">
        <PublicThemeWrapper inert={true}>
          {sectionPreviews.map((section) => (
            <PageSectionRenderer key={section.id} section={section} renderMode={renderMode} />
          ))}
        </PublicThemeWrapper>
      </ScaledContent>
    </section>
  );
}
